// Servicio para comunicación con la API PHP
class ApiService {
    constructor() {
        // Detectar modo
        this.isWebMode = !(window.electron && window.electron.ipcRenderer);

        // URLs por defecto
        this.fallbackURL = 'http://localhost/api_pos/reportes/funciones_php/api.php';
        this.webFallbackURL = 'http://localhost/api_pos/reportes/funciones_php/api.php'; // Cambiar a URL del servidor nube
        this.baseURL = this.isWebMode ? this.webFallbackURL : this.fallbackURL;
        this.token = localStorage.getItem('auth_token');
        this.initialized = false;

        // Intentar cargar la URL del archivo de configuración
        this.initializeApiUrl();
    }

    // Inicializar la URL del API desde el archivo de configuración
    async initializeApiUrl() {
        try {
            if (!this.isWebMode) {
                // Electron: leer desde archivo de configuración
                const result = await window.electron.ipcRenderer.invoke('read-api-config');
                if (result.success && result.apiUrl) {
                    this.baseURL = result.apiUrl;
                    localStorage.setItem('api_url', result.apiUrl);
                    console.log('✅ API URL cargada desde archivo:', this.baseURL);
                } else if (result.apiUrl) {
                    this.baseURL = result.apiUrl;
                    console.log('ℹ️ Usando URL fallback:', this.baseURL);
                }
            } else {
                // Web: prioridad -> localStorage web_api_url > localStorage api_url > webFallbackURL
                const webUrl = localStorage.getItem('web_api_url');
                const savedUrl = localStorage.getItem('api_url');
                if (webUrl) {
                    this.baseURL = webUrl;
                    console.log('✅ API URL web cargada:', this.baseURL);
                } else if (savedUrl) {
                    this.baseURL = savedUrl;
                    console.log('✅ API URL cargada desde localStorage:', this.baseURL);
                } else {
                    console.log('ℹ️ Usando URL web fallback:', this.baseURL);
                }
            }
        } catch (error) {
            console.error('Error inicializando API URL:', error);
            const savedUrl = localStorage.getItem(this.isWebMode ? 'web_api_url' : 'api_url');
            if (savedUrl) {
                this.baseURL = savedUrl;
            }
        } finally {
            this.initialized = true;
        }
    }

    // Obtener la URL actual del API
    getApiUrl() {
        return this.baseURL;
    }

    // Obtener la URL fallback (para mostrar como referencia)
    getDefaultUrl() {
        return this.fallbackURL;
    }

    // Actualizar la URL del API (en memoria)
    setApiUrl(newUrl) {
        this.baseURL = newUrl;
        // También guardar en localStorage como backup
        localStorage.setItem('api_url', newUrl);
        console.log('✅ API URL actualizada:', this.baseURL);
    }

    // Guardar la URL en el archivo de configuración
    async saveApiUrl(apiUrl) {
        try {
            if (window.electron && window.electron.ipcRenderer) {
                const result = await window.electron.ipcRenderer.invoke('write-api-config', apiUrl);
                if (result.success) {
                    this.setApiUrl(apiUrl);
                    return { success: true, message: 'Configuración guardada' };
                } else {
                    return { success: false, message: result.error || 'Error al guardar' };
                }
            } else {
                // Fallback: guardar en localStorage
                localStorage.setItem('api_url', apiUrl);
                this.setApiUrl(apiUrl);
                return { success: true, message: 'Configuración guardada en localStorage' };
            }
        } catch (error) {
            console.error('Error guardando API URL:', error);
            return { success: false, message: error.message };
        }
    }

    // Probar conexión con una URL específica
    async testApiConnection(apiUrl) {
        try {
            if (window.electron && window.electron.ipcRenderer) {
                return await window.electron.ipcRenderer.invoke('test-api-connection', apiUrl);
            } else {
                // Fallback: probar conexión desde el navegador
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tipo: 'ping' })
                });
                return {
                    success: response.ok,
                    message: response.ok ? 'Conexión exitosa' : `Error HTTP: ${response.status}`,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    // Método para realizar peticiones HTTP
    async request(method, data = {}) {
        try {
            // Reintentar obtener token del localStorage si no lo tenemos
            if (!this.token) {
                this.token = localStorage.getItem('auth_token');
            }

            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data)
            };

            // Agregar token si existe
            if (this.token) {
                options.headers['Authorization'] = `Bearer ${this.token}`;
            }

            const response = await fetch(this.baseURL, options);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Si la respuesta indica que el token es inválido, limpiar localStorage
            if (result.message === 'Token inválido' || result.message === 'Token expirado') {
                this.token = null;
                localStorage.removeItem('auth_token');
                localStorage.removeItem('current_user');
            }

            return result;

        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }

    // Métodos de autenticación
    async login(usuario, password) {
        const result = await this.request('POST', {
            tipo: 'login',
            usuario,
            password
        });

        if (result.success && result.data.session_token) {
            this.token = result.data.session_token;
            localStorage.setItem('auth_token', this.token);
            localStorage.setItem('current_user', JSON.stringify(result.data));
        }

        return result;
    }

    async logout() {
        try {
            await this.request('POST', { tipo: 'logout' });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.token = null;
            localStorage.removeItem('auth_token');
            localStorage.removeItem('current_user');
        }
    }

    async refreshToken() {
        const result = await this.request('POST', { tipo: 'refreshToken' });
        if (result.success && result.data.session_token) {
            this.token = result.data.session_token;
            localStorage.setItem('auth_token', this.token);
        }
        return result;
    }

    // Método para verificar si hay un token válido
    isAuthenticated() {
        return !!this.token || !!localStorage.getItem('auth_token');
    }

    // Método para obtener el token actual
    getToken() {
        if (!this.token) {
            this.token = localStorage.getItem('auth_token');
        }
        return this.token;
    }

    // Método para limpiar token
    clearToken() {
        this.token = null;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('current_user');
    }

    // Métodos de usuarios
    async getUsuarios() {
        return await this.request('POST', { tipo: 'getUsuarios' });
    }

    async crearUsuario(userData) {
        return await this.request('POST', {
            tipo: 'crearUsuario',
            ...userData
        });
    }

    async actualizarUsuario(userData) {
        return await this.request('POST', {
            tipo: 'actualizarUsuario',
            ...userData
        });
    }

    async cambiarPassword(passwordData) {
        return await this.request('POST', {
            tipo: 'cambiarPassword',
            ...passwordData
        });
    }

    async eliminarUsuario(userData) {
        return await this.request('POST', {
            tipo: 'eliminarUsuario',
            ...userData
        });
    }

    // Métodos de roles
    async getRoles() {
        return await this.request('POST', { tipo: 'getRoles' });
    }

    async getRol(roleId) {
        return await this.request('POST', {
            tipo: 'getRol',
            role_id: roleId
        });
    }

    async crearRol(roleData) {
        return await this.request('POST', {
            tipo: 'crearRol',
            ...roleData
        });
    }

    async actualizarRol(roleData) {
        return await this.request('POST', {
            tipo: 'actualizarRol',
            ...roleData
        });
    }

    async eliminarRol(roleData) {
        return await this.request('POST', {
            tipo: 'eliminarRol',
            ...roleData
        });
    }

    async getPermisos() {
        return await this.request('POST', { tipo: 'getPermisos' });
    }

    // Métodos de productos
    async getProductos() {
        return await this.request('POST', { tipo: 'getProductos' });
    }

    async getProductosPaginados(params = {}) {
        const { pagina = 1, limite = 50, busqueda = '', categoria_id, incluir_descuentos } = params;
        return await this.request('POST', {
            tipo: 'getProductosPaginados',
            pagina,
            limite,
            busqueda,
            categoria_id,
            incluir_descuentos
        });
    }

    async getProductosParaPrecios(params = {}) {
        const { pagina = 1, limite = 50, busqueda = '', categoria_id } = params;
        return await this.request('POST', {
            tipo: 'getProductosParaPrecios',
            pagina,
            limite,
            busqueda,
            categoria_id
        });
    }

    async getProducto(id) {
        return await this.request('POST', {
            tipo: 'getProducto',
            id
        });
    }

    async buscarProductoPorCodigo(codigo) {
        return await this.request('POST', {
            tipo: 'getProductosPorCodigoBarras',
            codigo_barras: codigo
        });
    }

    async crearProducto(productData) {
        return await this.request('POST', {
            tipo: 'crearProducto',
            ...productData
        });
    }

    async actualizarProducto(productData) {
        return await this.request('POST', {
            tipo: 'actualizarProducto',
            ...productData
        });
    }

    async actualizarPrecios(id, precios) {
        return await this.request('POST', {
            tipo: 'actualizarPrecios',
            id,
            ...precios
        });
    }

    async eliminarProducto(id) {
        return await this.request('POST', {
            tipo: 'eliminarProducto',
            id
        });
    }

    async buscarProductos(query) {
        return await this.request('POST', {
            tipo: 'buscarProductos',
            query
        });
    }

    async actualizarImpresorasMasivo(productos) {
        return await this.request('POST', {
            tipo: 'actualizarImpresorasMasivo',
            productos
        });
    }

    async getProductosPorCodigoBarras(codigo) {
        return await this.request('POST', {
            tipo: 'getProductosPorCodigoBarras',
            codigo_barras: codigo
        });
    }

    // Métodos de equivalencias de productos
    async getEquivalenciasProducto(producto_id) {
        return await this.request('POST', {
            tipo: 'getEquivalenciasProducto',
            producto_id
        });
    }

    async getEquivalenciasBidireccionales(producto_id) {
        return await this.request('POST', {
            tipo: 'getEquivalenciasBidireccionales',
            producto_id
        });
    }

    async guardarEquivalenciasProducto(producto_id, equivalencias) {
        return await this.request('POST', {
            tipo: 'guardarEquivalenciasProducto',
            producto_id,
            equivalencias
        });
    }

    async eliminarEquivalencia(id) {
        return await this.request('POST', {
            tipo: 'eliminarEquivalencia',
            id
        });
    }

    // Métodos de descuentos por volumen
    async getDescuentosVolumen(producto_id) {
        return await this.request('POST', {
            tipo: 'getDescuentosVolumen',
            producto_id
        });
    }

    async guardarDescuentosVolumen(producto_id, descuentos) {
        return await this.request('POST', {
            tipo: 'guardarDescuentosVolumen',
            producto_id,
            descuentos
        });
    }

    async eliminarDescuentoVolumen(id) {
        return await this.request('POST', {
            tipo: 'eliminarDescuentoVolumen',
            id
        });
    }

    async calcularPrecioConDescuento(producto_id, cantidad) {
        return await this.request('POST', {
            tipo: 'calcularPrecioConDescuento',
            producto_id,
            cantidad
        });
    }

    async getDescuentosVolumenMultiples(producto_ids) {
        return await this.request('POST', {
            tipo: 'getDescuentosVolumenMultiples',
            producto_ids
        });
    }

    async updateDescuentoVolumen(producto_id, cantidad_minima, descuento_porcentaje = null) {
        const data = {
            tipo: 'updateDescuentoVolumen',
            producto_id,
            cantidad_minima
        };
        if (descuento_porcentaje !== null) {
            data.descuento_porcentaje = descuento_porcentaje;
        }
        return await this.request('POST', data);
    }

    // === PRECIOS RAY (permiso: precios_ray) ===
    async getProductosParaRay(params = {}) {
        const { pagina = 1, limite = 50, busqueda = '', categoria_id } = params;
        return await this.request('POST', {
            tipo: 'getProductosParaRay',
            pagina,
            limite,
            busqueda,
            categoria_id
        });
    }

    async actualizarPreciosRay(id, precios) {
        return await this.request('POST', {
            tipo: 'actualizarPreciosRay',
            id,
            ...precios
        });
    }

    async updateDescuentoVolumenRay(producto_id, cantidad_minima, descuento_porcentaje = null) {
        const data = {
            tipo: 'updateDescuentoVolumenRay',
            producto_id,
            cantidad_minima
        };
        if (descuento_porcentaje !== null) {
            data.descuento_porcentaje = descuento_porcentaje;
        }
        return await this.request('POST', data);
    }

    // Métodos de rangos de precios
    async getConfiguracionRangos() {
        return await this.request('POST', {
            tipo: 'getConfiguracionRangos'
        });
    }

    async actualizarConfiguracionRango(rangoData) {
        return await this.request('POST', {
            tipo: 'actualizarConfiguracionRango',
            ...rangoData
        });
    }

    async getPrecioPorCantidad(producto_id, cantidad, cliente_id = null) {
        return await this.request('POST', {
            tipo: 'getPrecioPorCantidad',
            producto_id,
            cantidad,
            cliente_id
        });
    }

    async aplicarRangosAProducto(producto_id) {
        return await this.request('POST', {
            tipo: 'aplicarRangosAProducto',
            producto_id
        });
    }

    async guardarRangosProducto(producto_id, rangos) {
        return await this.request('POST', {
            tipo: 'guardarRangosProducto',
            producto_id,
            rangos
        });
    }

    async getRangosProducto(producto_id) {
        return await this.request('POST', {
            tipo: 'getRangosProducto',
            producto_id
        });
    }

    // Métodos de categorías
    async getCategorias() {
        return await this.request('POST', { tipo: 'getCategorias' });
    }

    async crearCategoria(categoryData) {
        return await this.request('POST', {
            tipo: 'crearCategoria',
            ...categoryData
        });
    }

    async actualizarCategoria(categoryData) {
        return await this.request('POST', {
            tipo: 'actualizarCategoria',
            ...categoryData
        });
    }

    async eliminarCategoria(id) {
        return await this.request('POST', {
            tipo: 'eliminarCategoria',
            id
        });
    }

    // Métodos de ventas
    async crearVenta(ventaData) {
        return await this.request('POST', {
            tipo: 'crearVenta',
            ...ventaData
        });
    }

    async getVentas(filtros = {}) {
        return await this.request('POST', { tipo: 'getVentas', sucursal: filtros.sucursal || '' });
    }

    async getVentasDelDia() {
        return await this.request('POST', { tipo: 'getVentasDelDia' });
    }

    async getVentasPorFecha(fechaInicio, fechaFin) {
        return await this.request('POST', {
            tipo: 'getVentasPorFecha',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        });
    }

    async getVenta(id) {
        return await this.request('POST', {
            tipo: 'getVenta',
            id
        });
    }

    async actualizarEstadoVenta(id, estado) {
        return await this.request('POST', {
            tipo: 'actualizarEstadoVenta',
            id,
            estado
        });
    }

    async actualizarPagoVenta(id, metodo_pago, estatus_pago) {
        return await this.request('POST', {
            tipo: 'actualizarPagoVenta',
            id,
            metodo_pago,
            estatus_pago
        });
    }

    async actualizarEstatusPreparacion(venta_detalle_id, estatus_preparacion, incrementar_cantidad = true, completar_total = false) {
        return await this.request('POST', {
            tipo: 'actualizarEstatusPreparacion',
            venta_detalle_id,
            estatus_preparacion,
            incrementar_cantidad,
            completar_total
        });
    }

    async reiniciarEscaneoProducto(venta_detalle_id) {
        return await this.request('POST', {
            tipo: 'reiniciarEscaneoProducto',
            venta_detalle_id
        });
    }

    async getVentasPorEstadoYAlmacen(estado, almacen_id) {
        return await this.request('POST', {
            tipo: 'getVentasPorEstadoYAlmacen',
            estado,
            almacen_id
        });
    }

    async imprimirTicket(venta_id) {
        return await this.request('POST', {
            tipo: 'imprimirTicket',
            venta_id
        });
    }

    // Métodos de inventario
    async getInventario() {
        return await this.request('POST', { tipo: 'getInventario' });
    }

    async actualizarStock(stockData) {
        return await this.request('POST', {
            tipo: 'actualizarStock',
            ...stockData
        });
    }

    async getProductosBajoStock() {
        return await this.request('POST', { tipo: 'getProductosBajoStock' });
    }

    // Métodos de reportes
    async getReporteVentas(fechaInicio, fechaFin, metodoPago = '', estado = '', sucursalId = '') {
        const data = {
            tipo: 'getReporteVentas',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            metodo_pago: metodoPago,
            estado: estado
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteVentasPorMetodoPago(fechaInicio, fechaFin, sucursalId = '') {
        const data = {
            tipo: 'getReporteVentasPorMetodoPago',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteProductosMasVendidos(fechaInicio, fechaFin, limite = 10, sucursalId = '') {
        const data = {
            tipo: 'getReporteProductosMasVendidos',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            limite: limite
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteVentasPorUsuario(fechaInicio, fechaFin, sucursalId = '') {
        const data = {
            tipo: 'getReporteVentasPorUsuario',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteResumenDiario(fechaInicio, fechaFin) {
        return await this.request('POST', {
            tipo: 'getReporteResumenDiario',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        });
    }

    async getReporteInventario() {
        return await this.request('POST', { tipo: 'getReporteInventario' });
    }

    async getEstadisticasGenerales() {
        return await this.request('POST', { tipo: 'getEstadisticasGenerales' });
    }

    // Métodos de reportes de ventas de mostrador
    async getReporteVentasMostrador(fechaInicio, fechaFin, metodoPago = '', usuarioId = null, cajaId = null, sucursalId = '') {
        const data = {
            tipo: 'getReporteVentasMostrador',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            metodo_pago: metodoPago,
            usuario_id: usuarioId,
            caja_id: cajaId
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteVentasMostradorPorUsuario(fechaInicio, fechaFin, sucursalId = '') {
        const data = {
            tipo: 'getReporteVentasMostradorPorUsuario',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteVentasMostradorPorCaja(fechaInicio, fechaFin, sucursalId = '') {
        const data = {
            tipo: 'getReporteVentasMostradorPorCaja',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getReporteProductosMostradorMasVendidos(fechaInicio, fechaFin, limite = 20, sucursalId = '') {
        const data = {
            tipo: 'getReporteProductosMostradorMasVendidos',
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            limite: limite
        };
        if (sucursalId) data.sucursal = sucursalId;
        return await this.request('POST', data);
    }

    async getVentasMostradorDetalle(ventaId) {
        return await this.request('POST', {
            tipo: 'getVentasMostradorDetalle',
            venta_id: ventaId
        });
    }

    async getHistorialProductoCliente(productoId, clienteId, limite = 5) {
        return await this.request('POST', {
            tipo: 'getHistorialProductoCliente',
            producto_id: productoId,
            cliente_id: clienteId,
            limite
        });
    }

    // Métodos de dashboard
    async getDashboardData() {
        return await this.request('POST', { tipo: 'getDashboardData' });
    }

    async getVentasHoy() {
        return await this.request('POST', { tipo: 'getVentasHoy' });
    }

    // Métodos de configuración
    async getConfiguracion() {
        return await this.request('POST', { tipo: 'getConfiguracion' });
    }

    async actualizarConfiguracion(configData) {
        return await this.request('POST', {
            tipo: 'actualizarConfiguracion',
            ...configData
        });
    }

    // Método para verificar estado de la API
    async checkApiStatus() {
        try {
            const response = await fetch(this.baseURL);
            return await response.json();
        } catch (error) {
            throw new Error('No se puede conectar con la API');
        }
    }

    // Métodos de vehículos
    async getVehiculos() {
        return await this.request('POST', { tipo: 'getVehiculos' });
    }

    async crearVehiculo(vehiculoData) {
        return await this.request('POST', {
            tipo: 'crearVehiculo',
            ...vehiculoData
        });
    }

    async actualizarVehiculo(vehiculoData) {
        return await this.request('POST', {
            tipo: 'actualizarVehiculo',
            ...vehiculoData
        });
    }

    async eliminarVehiculo(id) {
        return await this.request('POST', {
            tipo: 'eliminarVehiculo',
            id
        });
    }

    // Métodos de rangos de flete
    async getRangosFlete() {
        return await this.request('POST', { tipo: 'getRangosFlete' });
    }

    async crearRangoFlete(rangoData) {
        return await this.request('POST', {
            tipo: 'crearRangoFlete',
            ...rangoData
        });
    }

    async actualizarRangoFlete(rangoData) {
        return await this.request('POST', {
            tipo: 'actualizarRangoFlete',
            ...rangoData
        });
    }

    async eliminarRangoFlete(id) {
        return await this.request('POST', {
            tipo: 'eliminarRangoFlete',
            id
        });
    }

    // === MÉTODOS DE CLIENTES ===

    // Obtener todos los clientes
    async getClientes() {
        return await this.request('POST', { tipo: 'getClientes' });
    }

    // Obtener un cliente específico
    async getCliente(id) {
        return await this.request('POST', { tipo: 'getCliente', id });
    }

    // Crear cliente
    async crearCliente(clienteData) {
        return await this.request('POST', {
            tipo: 'crearCliente',
            ...clienteData
        });
    }

    // Actualizar cliente
    async actualizarCliente(clienteData) {
        return await this.request('POST', {
            tipo: 'actualizarCliente',
            ...clienteData
        });
    }

    // Eliminar (desactivar) cliente
    async eliminarCliente(id) {
        return await this.request('POST', {
            tipo: 'eliminarCliente',
            id
        });
    }

    // Buscar clientes
    async buscarClientes(query) {
        return await this.request('POST', {
            tipo: 'buscarClientes',
            query
        });
    }

    // Buscar cliente por teléfono
    async buscarClientePorTelefono(telefono) {
        return await this.request('POST', {
            tipo: 'buscarClientePorTelefono',
            telefono
        });
    }

    // === MÉTODOS DE ALMACENES ===

    // Obtener todos los almacenes
    async getAlmacenes() {
        return await this.request('POST', {
            tipo: 'getAlmacenes'
        });
    }

    // Obtener almacén por ID
    async getAlmacenById(id) {
        return await this.request('POST', {
            tipo: 'getAlmacenById',
            id
        });
    }

    // Crear almacén
    async crearAlmacen(almacenData) {
        return await this.request('POST', {
            tipo: 'crearAlmacen',
            ...almacenData
        });
    }

    // Actualizar almacén
    async actualizarAlmacen(almacenData) {
        return await this.request('POST', {
            tipo: 'actualizarAlmacen',
            ...almacenData
        });
    }

    // Eliminar (desactivar) almacén
    async eliminarAlmacen(id) {
        return await this.request('POST', {
            tipo: 'eliminarAlmacen',
            id
        });
    }

    // === MÉTODOS DE VENTAS DE MOSTRADOR ===

    // Crear venta de mostrador
    async crearVentaMostrador(ventaData) {
        return await this.request('POST', {
            tipo: 'crearVentaMostrador',
            ...ventaData
        });
    }

    // Obtener todas las ventas de mostrador
    async getVentasMostrador() {
        return await this.request('POST', {
            tipo: 'getVentasMostrador'
        });
    }

    // Obtener una venta de mostrador específica
    async getVentaMostrador(id) {
        return await this.request('POST', {
            tipo: 'getVentaMostrador',
            id
        });
    }

    // Imprimir ticket de venta de mostrador
    async imprimirTicketMostrador(id) {
        return await this.request('POST', {
            tipo: 'imprimirTicketMostrador',
            id
        });
    }

    // === DEVOLUCIONES ===

    // Buscar venta por folio para devolución
    async buscarVentaPorFolio(folio) {
        return await this.request('POST', {
            tipo: 'buscarVentaPorFolio',
            folio
        });
    }

    // Procesar devolución de artículos
    async procesarDevolucion(devolucionData) {
        return await this.request('POST', {
            tipo: 'procesarDevolucion',
            ...devolucionData
        });
    }

    // === CORTES DE CAJA ===

    // Obtener ventas pendientes de corte
    async getVentasPendientesCorte(nombrePc) {
        return await this.request('POST', {
            tipo: 'getVentasPendientesCorte',
            nombre_pc: nombrePc
        });
    }

    // Crear corte de caja
    async crearCorte(corteData) {
        return await this.request('POST', {
            tipo: 'crearCorte',
            ...corteData
        });
    }

    // Obtener historial de cortes
    async getHistorialCortes(nombrePc, limite = 20, offset = 0) {
        return await this.request('POST', {
            tipo: 'getHistorialCortes',
            nombre_pc: nombrePc,
            limite,
            offset
        });
    }

    // Obtener detalle de un corte
    async getDetalleCorte(corteId) {
        return await this.request('POST', {
            tipo: 'getDetalleCorte',
            corte_id: corteId
        });
    }

    // Imprimir ticket de corte
    async imprimirCorte(corteId) {
        return await this.request('POST', {
            tipo: 'imprimirCorte',
            corte_id: corteId
        });
    }

    // === RETIROS DE EFECTIVO ===

    // Verificar permiso para retiro de efectivo (busca usuario por contraseña)
    async verificarPermisoRetiro(password) {
        return await this.request('POST', {
            tipo: 'verificarPermisoRetiro',
            password: password
        });
    }

    // Verificar permiso para devolución (busca usuario por contraseña)
    async verificarPermisoDevolucion(password) {
        return await this.request('POST', {
            tipo: 'verificarPermisoDevolucion',
            password: password
        });
    }

    // Verificar permiso para precio libre (busca usuario por contraseña)
    async verificarPermisoPrecioLibre(password) {
        return await this.request('POST', {
            tipo: 'verificarPermisoPrecioLibre',
            password: password
        });
    }

    // Crear retiro de efectivo
    async crearRetiroEfectivo(retiroData) {
        return await this.request('POST', {
            tipo: 'crearRetiroEfectivo',
            ...retiroData
        });
    }

    // Obtener retiros de efectivo
    async getRetirosEfectivo(nombrePc, fechaInicio = null, fechaFin = null) {
        return await this.request('POST', {
            tipo: 'getRetirosEfectivo',
            nombre_pc: nombrePc,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        });
    }

    // Obtener total de retiros pendientes de corte
    async getRetirosPendientesCorte(nombrePc) {
        return await this.request('POST', {
            tipo: 'getRetirosPendientesCorte',
            nombre_pc: nombrePc
        });
    }

    // === INGRESOS DE EFECTIVO ===

    // Verificar permiso para ingreso de efectivo
    async verificarPermisoIngreso(password) {
        return await this.request('POST', {
            tipo: 'verificarPermisoIngreso',
            password: password
        });
    }

    // Crear ingreso de efectivo
    async crearIngresoEfectivo(ingresoData) {
        return await this.request('POST', {
            tipo: 'crearIngresoEfectivo',
            ...ingresoData
        });
    }

    // Obtener ingresos de efectivo
    async getIngresosEfectivo(nombrePc, fechaInicio = null, fechaFin = null) {
        return await this.request('POST', {
            tipo: 'getIngresosEfectivo',
            nombre_pc: nombrePc,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        });
    }

    // Obtener total de ingresos pendientes de corte
    async getIngresosPendientesCorte(nombrePc) {
        return await this.request('POST', {
            tipo: 'getIngresosPendientesCorte',
            nombre_pc: nombrePc
        });
    }

    // === MÉTODOS DE PROVEEDORES ===

    async getProveedores() {
        return await this.request('POST', { tipo: 'getProveedores' });
    }

    async getProveedor(id) {
        return await this.request('POST', { tipo: 'getProveedor', id });
    }

    async crearProveedor(proveedorData) {
        return await this.request('POST', {
            tipo: 'crearProveedor',
            ...proveedorData
        });
    }

    async actualizarProveedor(proveedorData) {
        return await this.request('POST', {
            tipo: 'actualizarProveedor',
            ...proveedorData
        });
    }

    async eliminarProveedor(id) {
        return await this.request('POST', {
            tipo: 'eliminarProveedor',
            id
        });
    }

    async buscarProveedores(query) {
        return await this.request('POST', {
            tipo: 'buscarProveedores',
            query
        });
    }

    // === MÉTODOS DE ÓRDENES DE COMPRA ===

    async getOrdenesCompra(estado = '') {
        return await this.request('POST', {
            tipo: 'getOrdenesCompra',
            estado
        });
    }

    async getOrdenCompra(id) {
        return await this.request('POST', {
            tipo: 'getOrdenCompra',
            id
        });
    }

    async crearOrdenCompra(ordenData) {
        return await this.request('POST', {
            tipo: 'crearOrdenCompra',
            ...ordenData
        });
    }

    async actualizarOrdenCompra(ordenData) {
        return await this.request('POST', {
            tipo: 'actualizarOrdenCompra',
            ...ordenData
        });
    }

    async eliminarOrdenCompra(id) {
        return await this.request('POST', {
            tipo: 'eliminarOrdenCompra',
            id
        });
    }

    async enviarAprobacionOrden(id) {
        return await this.request('POST', {
            tipo: 'enviarAprobacionOrden',
            id
        });
    }

    async aprobarOrdenCompra(id, aprobado_por) {
        return await this.request('POST', {
            tipo: 'aprobarOrdenCompra',
            id,
            aprobado_por
        });
    }

    async rechazarOrdenCompra(id, motivo, rechazado_por) {
        return await this.request('POST', {
            tipo: 'rechazarOrdenCompra',
            id,
            motivo,
            rechazado_por
        });
    }

    async recibirOrdenCompra(id, productos, recibido_por, estado_pago = 'pendiente') {
        return await this.request('POST', {
            tipo: 'recibirOrdenCompra',
            id,
            productos,
            recibido_por,
            estado_pago
        });
    }

    async cancelarOrdenCompra(id, motivo) {
        return await this.request('POST', {
            tipo: 'cancelarOrdenCompra',
            id,
            motivo
        });
    }

    async getOrdenesCompraPorEstado(estado) {
        return await this.request('POST', {
            tipo: 'getOrdenesCompraPorEstado',
            estado
        });
    }

    async getOrdenesCompraPorProveedor(proveedor_id) {
        return await this.request('POST', {
            tipo: 'getOrdenesCompraPorProveedor',
            proveedor_id
        });
    }

    async getOrdenesCompraPorFecha(fecha_inicio, fecha_fin) {
        return await this.request('POST', {
            tipo: 'getOrdenesCompraPorFecha',
            fecha_inicio,
            fecha_fin
        });
    }

    // ============== CUENTAS POR PAGAR (CXP) ==============

    async getCuentasPorPagar(filtros = {}) {
        return await this.request('POST', {
            tipo: 'getCuentasPorPagar',
            ...filtros
        });
    }

    async marcarComoPagada(id, pagado_por = null) {
        return await this.request('POST', {
            tipo: 'marcarComoPagada',
            id,
            pagado_por
        });
    }

    async marcarVariasComoPagadas(ids) {
        return await this.request('POST', {
            tipo: 'marcarVariasComoPagadas',
            ids
        });
    }

    async getResumenCXPPorProveedor() {
        return await this.request('POST', {
            tipo: 'getResumenCXPPorProveedor'
        });
    }

    // ============== CUENTAS POR COBRAR (CXC) ==============

    async getCuentasPorCobrar(filtros = {}) {
        return await this.request('POST', {
            tipo: 'getCuentasPorCobrar',
            ...filtros
        });
    }

    async registrarPagoCXC(pagoData) {
        return await this.request('POST', {
            tipo: 'registrarPagoCXC',
            ...pagoData
        });
    }

    async getHistorialPagosVenta(venta_id) {
        return await this.request('POST', {
            tipo: 'getHistorialPagosVenta',
            venta_id
        });
    }

    async getResumenCXCPorCliente() {
        return await this.request('POST', {
            tipo: 'getResumenCXCPorCliente'
        });
    }

    async getDetalleCuentaCXC(venta_id) {
        return await this.request('POST', {
            tipo: 'getDetalleCuentaCXC',
            venta_id
        });
    }

    async getCuentasPendientesCliente(cliente_id) {
        return await this.request('POST', {
            tipo: 'getCuentasPendientesCliente',
            cliente_id
        });
    }

    async registrarAbonoGlobalCXC(datos) {
        return await this.request('POST', {
            tipo: 'registrarAbonoGlobalCXC',
            cliente_id: datos.cliente_id,
            monto: datos.monto,
            metodo_pago: datos.metodo_pago || 'efectivo',
            referencia: datos.referencia || '',
            notas: datos.notas || '',
            registrado_por: datos.registrado_por
        });
    }

    async getPagosClientes(filtros = {}) {
        return await this.request('POST', {
            tipo: 'getPagosClientes',
            cliente_id: filtros.cliente_id || null,
            fecha_inicio: filtros.fecha_inicio || null,
            fecha_fin: filtros.fecha_fin || null,
            solo_sin_facturar: filtros.solo_sin_facturar || false,
            sucursal: filtros.sucursal || ''
        });
    }

    async getDetallePagoCliente(pago_id) {
        return await this.request('POST', {
            tipo: 'getDetallePagoCliente',
            pago_id
        });
    }

    async marcarPagoFacturado(pago_id, factura_id = null) {
        return await this.request('POST', {
            tipo: 'marcarPagoFacturado',
            pago_id,
            factura_id
        });
    }

    // === MÉTODOS DE RUTAS DE ENTREGA ===

    async getRutasEntrega(fechaInicio = null, fechaFin = null) {
        const hoy = new Date().toISOString().split('T')[0];
        return await this.request('POST', {
            tipo: 'getRutasEntrega',
            fecha_inicio: fechaInicio || hoy,
            fecha_fin: fechaFin || hoy
        });
    }

    async getRutaEntrega(id) {
        return await this.request('POST', {
            tipo: 'getRutaEntrega',
            id
        });
    }

    async crearRutaEntrega(nombre, fecha = null) {
        return await this.request('POST', {
            tipo: 'crearRutaEntrega',
            nombre,
            fecha: fecha || new Date().toISOString().split('T')[0]
        });
    }

    async actualizarRutaEntrega(id, datos) {
        return await this.request('POST', {
            tipo: 'actualizarRutaEntrega',
            id,
            ...datos
        });
    }

    async eliminarRutaEntrega(id) {
        return await this.request('POST', {
            tipo: 'eliminarRutaEntrega',
            id
        });
    }

    async agregarPedidosARuta(ruta_id, venta_ids) {
        return await this.request('POST', {
            tipo: 'agregarPedidosARuta',
            ruta_id,
            venta_ids
        });
    }

    async actualizarOrdenRuta(ruta_id, pedidos) {
        return await this.request('POST', {
            tipo: 'actualizarOrdenRuta',
            ruta_id,
            pedidos
        });
    }

    async quitarPedidoDeRuta(ruta_id, venta_id) {
        return await this.request('POST', {
            tipo: 'quitarPedidoDeRuta',
            ruta_id,
            venta_id
        });
    }

    async getPedidosDomicilioSinRuta(fechaInicio = null, fechaFin = null) {
        const hoy = new Date().toISOString().split('T')[0];
        return await this.request('POST', {
            tipo: 'getPedidosDomicilioSinRuta',
            fecha_inicio: fechaInicio || hoy,
            fecha_fin: fechaFin || hoy
        });
    }

    async imprimirCaratula(ruta_id) {
        return await this.request('POST', {
            tipo: 'imprimirCaratula',
            ruta_id
        });
    }

    // =============== CONFIGURACIONES ===============

    async getConfiguraciones(nombrePc = null) {
        return await this.request('POST', {
            tipo: 'getConfiguraciones',
            nombre_pc: nombrePc
        });
    }

    async getConfiguracion(clave, nombrePc = null) {
        return await this.request('POST', {
            tipo: 'getConfiguracion',
            clave,
            nombre_pc: nombrePc
        });
    }

    async getConfiguracionesPorCategoria(categoria, nombrePc = null) {
        return await this.request('POST', {
            tipo: 'getConfiguracionesPorCategoria',
            categoria,
            nombre_pc: nombrePc
        });
    }

    async actualizarConfiguracion(clave, valor, nombrePc = null) {
        return await this.request('POST', {
            tipo: 'actualizarConfiguracion',
            clave,
            valor,
            nombre_pc: nombrePc
        });
    }

    async actualizarConfiguraciones(configuraciones, nombrePc = null) {
        return await this.request('POST', {
            tipo: 'actualizarConfiguraciones',
            configuraciones,
            nombre_pc: nombrePc
        });
    }

    // =============== CAJAS / PUNTOS DE VENTA ===============

    async registrarCaja(identificador, nombrePc, ip) {
        return await this.request('POST', {
            tipo: 'registrarCaja',
            identificador,
            nombre_pc: nombrePc,
            ip
        });
    }

    async getCajaPorNombrePc(nombrePc) {
        return await this.request('POST', {
            tipo: 'getCajaPorNombrePc',
            nombre_pc: nombrePc
        });
    }

    async getCajas() {
        return await this.request('POST', {
            tipo: 'getCajas'
        });
    }

    // =============== FACTURACIÓN CFDI ===============

    // Catálogos SAT
    async getCatalogoRegimenFiscal() {
        return await this.request('POST', { tipo: 'getCatalogoRegimenFiscal' });
    }

    async getCatalogoUsoCFDI(personaTipo = null) {
        return await this.request('POST', {
            tipo: 'getCatalogoUsoCFDI',
            persona_tipo: personaTipo
        });
    }

    async getCatalogoFormaPago() {
        return await this.request('POST', { tipo: 'getCatalogoFormaPago' });
    }

    async getCatalogoMetodoPago() {
        return await this.request('POST', { tipo: 'getCatalogoMetodoPago' });
    }

    // Datos fiscales del cliente
    async getDatosFiscalesCliente(clienteId) {
        return await this.request('POST', {
            tipo: 'getDatosFiscalesCliente',
            cliente_id: clienteId
        });
    }

    async actualizarDatosFiscales(datosFiscales) {
        return await this.request('POST', {
            tipo: 'actualizarDatosFiscales',
            ...datosFiscales
        });
    }

    // Ventas facturables por cliente
    async getVentasFacturables(clienteId, fechaInicio = null, fechaFin = null) {
        return await this.request('POST', {
            tipo: 'getVentasFacturables',
            cliente_id: clienteId,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        });
    }

    // Ventas facturables general (con filtros opcionales)
    async getVentasFacturablesGeneral(filtros = {}) {
        return await this.request('POST', {
            tipo: 'getVentasFacturablesGeneral',
            cliente_id: filtros.cliente_id || '',
            fecha_inicio: filtros.fecha_inicio || '',
            fecha_fin: filtros.fecha_fin || '',
            folio: filtros.folio || '',
            sucursal: filtros.sucursal || ''
        });
    }

    // Configuración del emisor (legacy - único)
    async getConfiguracionEmisor() {
        return await this.request('POST', { tipo: 'getConfiguracionEmisor' });
    }

    async actualizarConfiguracionEmisor(emisorData) {
        return await this.request('POST', {
            tipo: 'actualizarConfiguracionEmisor',
            ...emisorData
        });
    }

    // Emisores múltiples
    async getEmisores() {
        return await this.request('POST', { tipo: 'getEmisores' });
    }

    async crearEmisor(emisorData) {
        return await this.request('POST', {
            tipo: 'crearEmisor',
            ...emisorData
        });
    }

    async actualizarEmisor(id, emisorData) {
        return await this.request('POST', {
            tipo: 'actualizarEmisor',
            id,
            ...emisorData
        });
    }

    async eliminarEmisor(id) {
        return await this.request('POST', {
            tipo: 'eliminarEmisor',
            id
        });
    }

    // CRUD Facturas
    async asignarClienteVentas(ventaIds, clienteId, tipoVenta = 'venta') {
        return await this.request('POST', {
            tipo: 'asignarClienteVentas',
            venta_ids: ventaIds,
            cliente_id: clienteId,
            tipo_venta: tipoVenta
        });
    }

    async crearFactura(facturaData) {
        const { tipo: tipoFactura, ...rest } = facturaData;
        return await this.request('POST', {
            tipo: 'crearFactura',
            tipo_factura: tipoFactura,
            ...rest
        });
    }

    async getFacturas(filtros = {}) {
        return await this.request('POST', {
            tipo: 'getFacturas',
            ...filtros
        });
    }

    async getFactura(id) {
        return await this.request('POST', {
            tipo: 'getFactura',
            id
        });
    }

    // Descargas
    async descargarFacturaXML(facturaId) {
        return await this.request('POST', {
            tipo: 'descargarFacturaXML',
            factura_id: facturaId
        });
    }

    async descargarFacturaPDF(facturaId) {
        return await this.request('POST', {
            tipo: 'descargarFacturaPDF',
            factura_id: facturaId
        });
    }

    // Reenvío
    async reenviarFactura(facturaId, email = null) {
        return await this.request('POST', {
            tipo: 'reenviarFactura',
            factura_id: facturaId,
            email
        });
    }

    // ==================== MIGRACION DE PRODUCTOS ====================

    // Probar conexión a base de datos externa
    async probarConexionExterna(conexion) {
        return await this.request('POST', {
            tipo: 'probarConexionExterna',
            ...conexion
        });
    }

    // Obtener columnas de una tabla
    async obtenerColumnasTabla(params) {
        return await this.request('POST', {
            tipo: 'obtenerColumnasTabla',
            ...params
        });
    }

    // Vista previa de migración
    async previewMigracion(params) {
        return await this.request('POST', {
            tipo: 'previewMigracion',
            ...params
        });
    }

    // Ejecutar migración de productos
    async ejecutarMigracionProductos(params) {
        return await this.request('POST', {
            tipo: 'ejecutarMigracionProductos',
            ...params
        });
    }

    // Preview migración Kerigma (sistema específico)
    async previewMigracionKerigma(params) {
        return await this.request('POST', {
            tipo: 'previewMigracionKerigma',
            ...params
        });
    }

    // Ejecutar migración Kerigma
    async ejecutarMigracionKerigma(params) {
        return await this.request('POST', {
            tipo: 'ejecutarMigracionKerigma',
            ...params
        });
    }

    // ==================== MIGRACION DE CLIENTES ====================

    // Preview migración clientes Kerigma
    async previewMigracionClientesKerigma(params) {
        return await this.request('POST', {
            tipo: 'previewMigracionClientesKerigma',
            ...params
        });
    }

    // Ejecutar migración clientes Kerigma
    async ejecutarMigracionClientesKerigma(params) {
        return await this.request('POST', {
            tipo: 'ejecutarMigracionClientesKerigma',
            ...params
        });
    }

    // ==================== DIFERENCIA DE COSTOS ====================

    // Preview diferencia de costos desde BD externa
    async previewDiferenciaCostos(params) {
        return await this.request('POST', {
            tipo: 'previewDiferenciaCostos',
            ...params
        });
    }

    // Ejecutar actualización de costos
    async ejecutarActualizacionCostos(params) {
        return await this.request('POST', {
            tipo: 'ejecutarActualizacionCostos',
            ...params
        });
    }

    // === SINCRONIZACIÓN ===
    async syncPush() {
        return await this.request('POST', { tipo: 'syncPush' });
    }

    async syncPull() {
        return await this.request('POST', { tipo: 'syncPull' });
    }

    async syncFullSync() {
        return await this.request('POST', { tipo: 'syncFullSync' });
    }

    async syncStatus() {
        return await this.request('POST', { tipo: 'syncStatus' });
    }

    async syncLog(limit = 50) {
        return await this.request('POST', { tipo: 'syncLog', limit });
    }

    async syncReset(target = 'both') {
        return await this.request('POST', { tipo: 'syncReset', target });
    }
}

// Crear instancia única del servicio
const apiService = new ApiService();

export default apiService;